import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import {
  AdminPageTitle,
  AdminHeaderAction,
  AdminFormFieldError,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminSupportingText,
  AdminFormDropdownChevron,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
  adminFormDetailTextareaClasses,
  ADMIN_PAGE_TITLE_CLASS,
} from '../shared/adminUx';
import {
  CONTROL_TYPE_OPTIONS,
  DATA_SOURCE_OPTIONS,
  FORM_CATEGORY_OPTIONS,
  FORM_INVENTORY_ROUTE,
  OWNING_MODULE_OPTIONS,
  RECORD_DESTINATION_OPTIONS,
  SAVED_SOURCE_RELATIONSHIP_OPTIONS,
  SELECTABLE_CONTROL_TYPES,
  SELECTABLE_VALUE_SOURCE_OPTIONS,
  USED_BY_OPTIONS,
  buildSampleFormInventory,
  createEmptyForm,
  createEmptyFormField,
  findFormByIdOrSlug,
  formatFormInventoryDateTime,
  getFieldKey,
  getFormKey,
} from '../../../data/formInventoryConfig';
import {
  DEFAULT_ACTOR,
  cloneFormRecord,
  controlNeedsSelectableSource,
  hasValidationErrors,
  mergeFormsAfterSave,
  validateFormRecord,
} from '../../../data/formInventoryWorkflow';

const ID_PLACEHOLDER = 'Generated after Save';

const TextValue = ({ label, value, mono = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    <AdminDetailFieldValue className={`py-0 break-words${mono ? ' font-mono' : ''}`}>
      {value || '—'}
    </AdminDetailFieldValue>
  </div>
);

const ReadOnlyField = ({ label, value, mono = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    <input
      type="text"
      value={value || '—'}
      readOnly
      className={`${adminFormDetailInputClasses(true)}${mono ? ' font-mono' : ''}`}
    />
  </div>
);

const StackedField = ({ label, required, error, children }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>
      {label}
      {required ? <span className="text-alertred"> *</span> : null}
    </AdminDetailFieldLabel>
    {children}
    {error ? <AdminFormFieldError>{error}</AdminFormFieldError> : null}
  </div>
);

const SelectInput = ({ value, options, onChange, placeholder, error }) => {
  const optionValues = options.map((opt) => (typeof opt === 'string' ? opt : opt.value));
  const mergedOptions =
    value && !optionValues.includes(value) ? [value, ...options] : options;
  return (
    <div className="relative">
      <select
        value={value || ''}
        onChange={(e) => onChange?.(e.target.value)}
        className={`${adminFormDetailSelectClasses(false, !!error)} appearance-none pr-8`}
      >
        <option value="">{placeholder || 'Select'}</option>
        {mergedOptions.map((opt) => {
          const val = typeof opt === 'string' ? opt : opt.value;
          const lab = typeof opt === 'string' ? opt : opt.label;
          return (
            <option key={val} value={val}>
              {lab}
            </option>
          );
        })}
      </select>
      <AdminFormDropdownChevron />
    </div>
  );
};

const MultiSelectBoxes = ({ values = [], options = [], onChange }) => (
  <div className="space-y-2">
    {options.map((opt) => {
      const checked = values.includes(opt);
      return (
        <label key={opt} className="flex items-center gap-2 text-sm text-offblack">
          <input
            type="checkbox"
            checked={checked}
            onChange={() => {
              if (checked) onChange(values.filter((v) => v !== opt));
              else onChange([...values, opt]);
            }}
          />
          <span>{opt}</span>
        </label>
      );
    })}
  </div>
);

const RepeatableTextList = ({ values = [''], onChange, placeholder }) => {
  const list = values.length ? values : [''];
  return (
    <div className="space-y-2">
      {list.map((value, index) => (
        <div key={`repeat-${index}`} className="flex items-start gap-2">
          <input
            type="text"
            value={value}
            onChange={(e) => {
              const next = [...list];
              next[index] = e.target.value;
              onChange(next);
            }}
            placeholder={placeholder}
            className={adminFormDetailInputClasses(false)}
          />
          {list.length > 1 ? (
            <button
              type="button"
              onClick={() => onChange(list.filter((_, i) => i !== index))}
              className="shrink-0 inline-flex items-center justify-center leading-none text-offblack hover:text-alertred mt-2.5"
              aria-label={`Remove item ${index + 1}`}
            >
              <i className="fi fi-rs-trash text-sm" aria-hidden />
            </button>
          ) : null}
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...list, ''])}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-primaryblue hover:text-brightblue"
      >
        <i className="fi fi-rs-plus text-xs" aria-hidden />
        Add
      </button>
    </div>
  );
};

const FieldBlock = ({
  field,
  index,
  formModes,
  errors = {},
  onChange,
  onRemove,
  canRemove,
  isView,
  isAdd,
}) => {
  const needsSelectable = controlNeedsSelectableSource(field.controlType);
  const source = field.selectableValueSource;

  if (isView) {
    return (
      <div className="rounded-xl border border-lightgray p-4 space-y-3">
        <AdminSupportingText className="mb-0 font-semibold !text-offblack">
          Form Field {index + 1}
        </AdminSupportingText>
        <TextValue label="Form Field ID" value={field.id || '—'} mono />
        <TextValue label="Field Label" value={field.fieldLabel} />
        <TextValue label="Field Key" value={getFieldKey(field)} mono />
        <TextValue label="Control Type" value={field.controlType} />
        <TextValue label="Display Order" value={String(field.displayOrder ?? '—')} />
        <TextValue
          label="Available Modes"
          value={(field.availableModes || []).join(', ')}
        />
        {needsSelectable ? (
          <>
            <TextValue label="Selectable Value Source" value={source} />
            {source === 'Fixed Form Values' ? (
              <TextValue
                label="Fixed Form Values"
                value={(field.fixedFormValues || []).filter(Boolean).join(', ')}
              />
            ) : null}
            {source === 'Saved Source Records' ? (
              <TextValue label="Saved Source Relationship" value={field.savedSourceRelationship} />
            ) : null}
          </>
        ) : null}
        <TextValue label="Placeholder" value={field.placeholder} />
        <TextValue label="Sample Value" value={field.sampleValue} />
        <TextValue label="Data Source" value={field.dataSource} />
        <TextValue
          label="Destination"
          value={field.destination || field.recordDestination}
        />
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-lightgray p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <AdminSupportingText className="mb-0 font-semibold !text-offblack">
          Form Field {index + 1}
        </AdminSupportingText>
        {canRemove ? (
          <button
            type="button"
            onClick={onRemove}
            className="shrink-0 inline-flex items-center justify-center leading-none text-offblack hover:text-alertred"
            aria-label={`Remove Form Field ${index + 1}`}
          >
            <i className="fi fi-rs-trash text-sm" aria-hidden />
          </button>
        ) : null}
      </div>

      {!isAdd ? (
        <ReadOnlyField label="Form Field ID" value={field.id || ID_PLACEHOLDER} mono />
      ) : null}

      <StackedField label="Field Label" required error={errors.fieldLabel}>
        <input
          type="text"
          value={field.fieldLabel || ''}
          onChange={(e) => onChange({ fieldLabel: e.target.value })}
          placeholder="Enter the displayed field label"
          className={adminFormDetailInputClasses(false, !!errors.fieldLabel)}
        />
      </StackedField>

      {field.id ? (
        <ReadOnlyField label="Field Key" value={getFieldKey(field)} mono />
      ) : (
        <StackedField label="Field Key" required error={errors.fieldKey}>
          <input
            type="text"
            value={getFieldKey(field)}
            onChange={(e) => onChange({ fieldKey: e.target.value })}
            placeholder="Enter the Field Key"
            className={adminFormDetailInputClasses(false, !!errors.fieldKey)}
          />
        </StackedField>
      )}

      <StackedField label="Control Type" required error={errors.controlType}>
        <SelectInput
          value={field.controlType}
          options={CONTROL_TYPE_OPTIONS}
          onChange={(v) => {
            const next = { controlType: v };
            if (!SELECTABLE_CONTROL_TYPES.includes(v)) {
              next.selectableValueSource = '';
              next.fixedFormValues = [];
              next.savedSourceRelationship = '';
            }
            onChange(next);
          }}
          placeholder="Select a control"
          error={errors.controlType}
        />
      </StackedField>

      <StackedField label="Display Order" required error={errors.displayOrder}>
        <input
          type="number"
          value={field.displayOrder ?? ''}
          onChange={(e) => onChange({ displayOrder: Number(e.target.value) })}
          placeholder="Enter the field position"
          className={adminFormDetailInputClasses(false, !!errors.displayOrder)}
        />
      </StackedField>

      <StackedField label="Available Modes" required error={errors.availableModes}>
        <MultiSelectBoxes
          values={field.availableModes || []}
          options={formModes}
          onChange={(v) => onChange({ availableModes: v })}
        />
      </StackedField>

      {needsSelectable ? (
        <>
          <StackedField
            label="Selectable Value Source"
            required
            error={errors.selectableValueSource}
          >
            <div className="space-y-2">
              {SELECTABLE_VALUE_SOURCE_OPTIONS.map((opt) => (
                <label key={opt} className="flex items-center gap-2 text-sm text-offblack">
                  <input
                    type="radio"
                    name={`selectable-source-${index}`}
                    checked={source === opt}
                    onChange={() =>
                      onChange({
                        selectableValueSource: opt,
                        fixedFormValues:
                          opt === 'Fixed Form Values'
                            ? field.fixedFormValues?.length
                              ? field.fixedFormValues
                              : ['']
                            : [],
                        savedSourceRelationship:
                          opt === 'Saved Source Records' ? field.savedSourceRelationship : '',
                      })
                    }
                  />
                  <span>{opt}</span>
                </label>
              ))}
            </div>
          </StackedField>

          {source === 'Fixed Form Values' ? (
            <StackedField label="Fixed Form Values" required error={errors.fixedFormValues}>
              <RepeatableTextList
                values={field.fixedFormValues?.length ? field.fixedFormValues : ['']}
                onChange={(v) => onChange({ fixedFormValues: v })}
                placeholder="Enter a fixed value"
              />
            </StackedField>
          ) : null}

          {source === 'Saved Source Records' ? (
            <StackedField
              label="Saved Source Relationship"
              required
              error={errors.savedSourceRelationship}
            >
              <SelectInput
                value={field.savedSourceRelationship}
                options={SAVED_SOURCE_RELATIONSHIP_OPTIONS}
                onChange={(v) => onChange({ savedSourceRelationship: v })}
                placeholder="Select the source register and record set"
                error={errors.savedSourceRelationship}
              />
            </StackedField>
          ) : null}
        </>
      ) : null}

      <StackedField label="Placeholder">
        <input
          type="text"
          value={field.placeholder || ''}
          onChange={(e) => onChange({ placeholder: e.target.value })}
          placeholder="Enter the operational placeholder"
          className={adminFormDetailInputClasses(false)}
        />
      </StackedField>

      <StackedField label="Sample Value" required error={errors.sampleValue}>
        <input
          type="text"
          value={field.sampleValue || ''}
          onChange={(e) => onChange({ sampleValue: e.target.value })}
          placeholder="Enter a realistic example"
          className={adminFormDetailInputClasses(false, !!errors.sampleValue)}
        />
      </StackedField>

      <StackedField label="Data Source" required error={errors.dataSource}>
        <SelectInput
          value={field.dataSource}
          options={DATA_SOURCE_OPTIONS}
          onChange={(v) => onChange({ dataSource: v })}
          placeholder="Select where the value comes from"
          error={errors.dataSource}
        />
      </StackedField>

      <StackedField label="Destination" required error={errors.destination}>
        <SelectInput
          value={field.destination || field.recordDestination}
          options={RECORD_DESTINATION_OPTIONS}
          onChange={(v) => onChange({ destination: v })}
          placeholder="Select the destination record field or request input"
          error={errors.destination}
        />
      </StackedField>
    </div>
  );
};

const FormInventoryDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const isAdd = location.pathname.endsWith('/add');
  const isEdit = location.pathname.endsWith('/edit');
  const isView = !isAdd && !isEdit;

  const initialForms = useMemo(
    () => (Array.isArray(location.state?.forms) ? location.state.forms : buildSampleFormInventory()),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [forms] = useState(initialForms);
  const [form, setForm] = useState(() => {
    if (isAdd) return createEmptyForm({ entryPoints: [''], modes: [''], fields: [] });
    const fromState = location.state?.form;
    if (fromState) return cloneFormRecord(fromState);
    const found = findFormByIdOrSlug(initialForms, params.formId);
    return found ? cloneFormRecord(found) : createEmptyForm();
  });
  const [errors, setErrors] = useState({});
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (isAdd) return;
    if (!form?.id && !location.state?.form) {
      const found = findFormByIdOrSlug(forms, params.formId);
      if (found) setForm(cloneFormRecord(found));
      else setNotFound(true);
    }
  }, [isAdd, form?.id, forms, params.formId, location.state?.form]);

  const updateForm = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const normalizeModes = (modes) =>
    (modes || []).map((m) => String(m || '').trim()).filter(Boolean);

  const updateField = (index, patch) => {
    setForm((prev) => {
      const fields = [...(prev.fields || [])];
      fields[index] = { ...fields[index], ...patch };
      return { ...prev, fields };
    });
  };

  const addField = () => {
    setForm((prev) => ({
      ...prev,
      fields: [
        ...(prev.fields || []),
        createEmptyFormField({
          displayOrder: (prev.fields || []).length + 1,
          availableModes: normalizeModes(prev.modes),
          fixedFormValues: [''],
        }),
      ],
    }));
  };

  const removeField = (index) => {
    setForm((prev) => ({
      ...prev,
      fields: (prev.fields || []).filter((_, i) => i !== index),
    }));
  };

  const handleSave = () => {
    const validation = validateFormRecord(form, forms, {
      excludeId: isAdd ? null : form.id,
    });
    setErrors(validation);
    if (hasValidationErrors(validation)) return;

    const { forms: nextForms, message } = mergeFormsAfterSave(forms, form, {
      isNew: isAdd,
      actor: DEFAULT_ACTOR,
    });
    navigate(FORM_INVENTORY_ROUTE, { state: { forms: nextForms, feedback: message } });
  };

  if (notFound) {
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Form Detail</AdminPageTitle>
        <AdminSupportingText className="text-mediumgray">
          The requested Form could not be located in Form Inventory.
        </AdminSupportingText>
      </AdminLayout>
    );
  }

  const pageTitle = isAdd ? 'Add Form' : isEdit ? 'Edit Form' : 'Form Detail';
  const fieldErrors = errors.fieldErrors || [];
  const formModes = normalizeModes(form.modes);
  const fieldBlocks = form.fields || [];

  return (
    <AdminLayout>
      <div className="max-w-[25rem] mx-auto w-full min-w-0">
        <div className="mb-6">
          <AdminPageTitle className="!mb-0">{pageTitle}</AdminPageTitle>
        </div>

        <div className="p-4 space-y-6 min-w-0">
          {isView ? (
            <>
              <TextValue label="Form ID" value={form.id} mono />
              <TextValue label="Form Key" value={getFormKey(form)} mono />
              <TextValue label="Form Name" value={form.formName} />
              <TextValue label="Purpose" value={form.purpose} />
              <TextValue label="Form Category" value={form.category} />
              <TextValue label="Owning Module" value={form.owningModule} />
              <TextValue label="Used By" value={(form.usedBy || []).join(', ')} />
              <TextValue label="Entry Points" value={(form.entryPoints || []).join(', ')} />
              <TextValue label="Modes" value={(form.modes || []).join(', ')} />
              <TextValue label="Field Count" value={String(fieldBlocks.length)} />
              <TextValue
                label="Date Added"
                value={form.dateAdded ? formatFormInventoryDateTime(form.dateAdded) : '—'}
              />
              <TextValue label="Added By" value={form.addedBy} />
              <TextValue
                label="Last Updated"
                value={form.lastUpdated ? formatFormInventoryDateTime(form.lastUpdated) : '—'}
              />
              <TextValue label="Last Updated By" value={form.lastUpdatedBy} />
            </>
          ) : (
            <>
              {isEdit ? (
                <ReadOnlyField label="Form ID" value={form.id} mono />
              ) : null}

              {isEdit ? (
                <ReadOnlyField label="Form Key" value={getFormKey(form)} mono />
              ) : (
                <StackedField label="Form Key" required error={errors.formKey}>
                  <input
                    type="text"
                    value={getFormKey(form)}
                    onChange={(e) => updateForm({ formKey: e.target.value })}
                    placeholder="Enter the Form Key"
                    className={adminFormDetailInputClasses(false, !!errors.formKey)}
                  />
                </StackedField>
              )}

              <StackedField label="Form Name" required error={errors.formName}>
                <input
                  type="text"
                  value={form.formName || ''}
                  onChange={(e) => updateForm({ formName: e.target.value })}
                  placeholder="Enter the form name"
                  className={adminFormDetailInputClasses(false, !!errors.formName)}
                />
              </StackedField>

              <StackedField label="Purpose" required error={errors.purpose}>
                <textarea
                  value={form.purpose || ''}
                  onChange={(e) => updateForm({ purpose: e.target.value })}
                  placeholder="Describe what the form submits or updates"
                  rows={4}
                  className={adminFormDetailTextareaClasses(false, !!errors.purpose)}
                />
              </StackedField>

              <StackedField label="Form Category" required error={errors.category}>
                <SelectInput
                  value={form.category}
                  options={FORM_CATEGORY_OPTIONS}
                  onChange={(v) => updateForm({ category: v })}
                  placeholder="Select a category"
                  error={errors.category}
                />
              </StackedField>

              <StackedField label="Owning Module" required error={errors.owningModule}>
                <SelectInput
                  value={form.owningModule}
                  options={OWNING_MODULE_OPTIONS}
                  onChange={(v) => updateForm({ owningModule: v })}
                  placeholder="Select the owning module"
                  error={errors.owningModule}
                />
              </StackedField>

              <StackedField label="Used By" required error={errors.usedBy}>
                <MultiSelectBoxes
                  values={form.usedBy || []}
                  options={USED_BY_OPTIONS}
                  onChange={(v) => updateForm({ usedBy: v })}
                />
              </StackedField>

              <StackedField label="Entry Points" required error={errors.entryPoints}>
                <RepeatableTextList
                  values={form.entryPoints?.length ? form.entryPoints : ['']}
                  onChange={(v) => updateForm({ entryPoints: v })}
                  placeholder="Enter the page, section, modal or drawer"
                />
              </StackedField>

              <StackedField label="Modes" required error={errors.modes}>
                <RepeatableTextList
                  values={form.modes?.length ? form.modes : ['']}
                  onChange={(v) => {
                    const cleaned = normalizeModes(v);
                    updateForm({
                      modes: v,
                      fields: (form.fields || []).map((field) => ({
                        ...field,
                        availableModes: (field.availableModes || []).filter((m) =>
                          cleaned.includes(m),
                        ),
                      })),
                    });
                  }}
                  placeholder="Enter a mode name"
                />
              </StackedField>

              {isEdit ? (
                <>
                  <ReadOnlyField label="Field Count" value={String(fieldBlocks.length)} />
                  <ReadOnlyField
                    label="Date Added"
                    value={
                      form.dateAdded ? formatFormInventoryDateTime(form.dateAdded) : '—'
                    }
                  />
                  <ReadOnlyField label="Added By" value={form.addedBy} />
                  <ReadOnlyField
                    label="Last Updated"
                    value={
                      form.lastUpdated ? formatFormInventoryDateTime(form.lastUpdated) : '—'
                    }
                  />
                  <ReadOnlyField label="Last Updated By" value={form.lastUpdatedBy} />
                </>
              ) : null}
            </>
          )}

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <AdminDetailFieldLabel className="!mb-0">Form Fields</AdminDetailFieldLabel>
              <AdminSupportingText className="mb-0">
                {fieldBlocks.length} added
              </AdminSupportingText>
            </div>
            {errors.fields ? <AdminFormFieldError>{errors.fields}</AdminFormFieldError> : null}
            <div className="space-y-3">
              {fieldBlocks.map((field, index) => (
                <FieldBlock
                  key={field.id || `new-field-${index}`}
                  field={field}
                  index={index}
                  formModes={formModes}
                  errors={fieldErrors[index] || {}}
                  canRemove={!isView && !field.id && fieldBlocks.length > 0}
                  isView={isView}
                  isAdd={isAdd}
                  onChange={(patch) => updateField(index, patch)}
                  onRemove={() => removeField(index)}
                />
              ))}
            </div>
            {!isView ? (
              <button
                type="button"
                onClick={addField}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primaryblue hover:text-brightblue"
              >
                <i className="fi fi-rs-plus text-xs" aria-hidden />
                Add Form Field
              </button>
            ) : null}
          </div>

          {!isView ? (
            <div className="flex items-center justify-end gap-3">
              <AdminHeaderAction size="lg" type="button" onClick={handleSave}>
                Save
              </AdminHeaderAction>
            </div>
          ) : null}
        </div>
      </div>
    </AdminLayout>
  );
};

export default FormInventoryDetail;
